import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Building2, Square, MessageCircle, Map, Eye, ChevronLeft, ChevronRight } from 'lucide-react';
import { ShareButton } from './ShareButton';
import { PropertyStatusBadge } from './PropertyStatusBadge';
import { motion } from 'framer-motion';
import type { Property } from '../../types/cms';
import { useCMS } from '../../context/CMSContext';
import { useTrackEvent } from '../../context/AnalyticsContext';
import { categoryLabels, formatPrice, getWhatsAppUrl, getShareUrl } from '../../utils/helpers';
import { MapModal } from './MapModal';

interface PropertyCardProps {
  property: Property;
  layout?: 'grid' | 'list';
}

export const PropertyCard: React.FC<PropertyCardProps> = ({ property, layout = 'grid' }) => {
  const { getAgent, cmsData } = useCMS();
  const trackEvent = useTrackEvent();
  const [showMap, setShowMap] = useState(false);
  const [activeImage, setActiveImage] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const agent = getAgent(property.agentId);
  const images = property.images.length > 0 ? property.images : [cmsData?.hero?.backgroundImage || ''];

  const displayPrice =
    property.listingType === 'rent' && property.rentPrice
      ? formatPrice(property.rentPrice, 'HKD / 月')
      : formatPrice(property.price, 'HKD');

  const whatsappMessage = `你好 ${agent?.name || ''}，我想查詢「${property.title}」（樓盤編號：${property.code}），請與我聯絡，謝謝。`;
  const whatsappUrl = agent ? getWhatsAppUrl(agent.whatsapp, whatsappMessage) : '#';

  const isGrid = layout === 'grid';

  return (
    <>
      <motion.article
        layout
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96 }}
        transition={{ duration: 0.28 }}
        className={`group overflow-hidden bg-white shadow-[0_16px_45px_-32px_rgba(15,23,42,0.6)] transition-shadow hover:shadow-[0_20px_50px_-26px_rgba(15,23,42,0.55)] ${
          isGrid ? '' : 'flex flex-col md:flex-row'
        }`}
      >
        <div
          className={`relative overflow-hidden ${isGrid ? 'h-60' : 'h-60 md:h-auto md:w-72 lg:w-80'}`}
          onTouchStart={(e) => setTouchStart(e.targetTouches[0].clientX)}
          onTouchMove={(e) => {
            if (touchStart === null) return;
            const diff = touchStart - e.targetTouches[0].clientX;
            if (Math.abs(diff) > 50) {
              e.preventDefault();
            }
          }}
          onTouchEnd={(e) => {
            if (touchStart === null) return;
            const diff = touchStart - e.changedTouches[0].clientX;
            if (diff > 50) {
              setActiveImage((prev) => (prev + 1) % images.length);
            } else if (diff < -50) {
              setActiveImage((prev) => (prev - 1 + images.length) % images.length);
            }
            setTouchStart(null);
          }}
        >
          <div
            className="flex h-full w-full transition-transform duration-500 ease-out"
            style={{ transform: `translateX(-${activeImage * 100}%)` }}
          >
            {images.map((img, idx) => (
              <img
                key={`${property.id}-${idx}`}
                src={img}
                alt={`${property.title} ${idx + 1}`}
                className="h-full w-full shrink-0 object-cover"
                draggable={false}
              />
            ))}
          </div>

          {/* 左上角張數計數器 */}
          <div className="absolute left-3 top-3 z-10">
            <span className="bg-slate-900/90 px-2.5 py-1 text-xs font-bold tracking-wide text-white backdrop-blur-sm">
              {activeImage + 1}/{images.length}
            </span>
          </div>

          {/* 右上角分類 badge */}
          <div className="absolute right-3 top-3 z-10">
            <span className="bg-white/95 px-3 py-1.5 text-xs font-bold text-blue-800 shadow-sm">
              {categoryLabels[property.category]}
            </span>
          </div>

          {/* 左下角狀態 / 租售類型 badge */}
          <div className="absolute bottom-3 left-3 z-10">
            {property.status !== 'available' ? (
              <PropertyStatusBadge status={property.status} className="px-3 py-1.5 text-xs" pulse={false} />
            ) : (
              <span className="bg-slate-900/90 px-3 py-1.5 text-xs font-bold tracking-wide text-white backdrop-blur-sm">
                {property.listingType === 'sale' ? '放售' : property.listingType === 'rent' ? '放租' : '租售均可'}
              </span>
            )}
          </div>

          {/* 左右切換箭頭（多於一張時顯示） */}
          {images.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setActiveImage((prev) => (prev - 1 + images.length) % images.length);
                }}
                className="absolute left-2 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/90 p-1.5 text-slate-700 opacity-0 shadow-sm transition-opacity hover:bg-white group-hover:opacity-100"
                aria-label="上一張"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setActiveImage((prev) => (prev + 1) % images.length);
                }}
                className="absolute right-2 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/90 p-1.5 text-slate-700 opacity-0 shadow-sm transition-opacity hover:bg-white group-hover:opacity-100"
                aria-label="下一張"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </>
          )}
        </div>

        <div className="flex flex-1 flex-col p-6">
          <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-slate-500">
            <span>樓盤編號 {property.code}</span>
            <span className="h-1 w-1 rounded-full bg-slate-300" />
            <span>{property.location}</span>
          </div>
          <h4 className="mb-3 text-xl font-bold text-slate-900 transition-colors group-hover:text-blue-800">
            {property.title}
          </h4>
          <div className="mb-5 flex items-center gap-2 text-sm text-slate-500">
            <MapPin className="h-4 w-4 shrink-0" />
            <span className="truncate">{property.address}</span>
          </div>

          <div className="mb-5 flex flex-wrap items-center justify-between gap-5 border-y border-slate-100 py-4 text-sm text-slate-700">
            <div className="flex flex-wrap items-center gap-5">
              <span className="flex items-center gap-2">
                <Square className="h-4 w-4 text-gold-700" />
                約 {property.areaGross.toLocaleString()} 呎
              </span>
              <span className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-gold-700" />
                {categoryLabels[property.category]}
              </span>
            </div>
            {isGrid && (
              <div className="flex items-center gap-4">
                <ShareButton
                  url={getShareUrl(property.id)}
                  title={property.title}
                  description={`${property.location} · ${property.address}`}
                />
                <button
                  type="button"
                  onClick={() => setShowMap(true)}
                  className="flex min-h-touch items-center gap-1 text-[1.3125rem] font-bold text-slate-600 hover:text-blue-800"
                >
                  <Map className="h-5 w-5" />
                  地圖
                </button>
                <Link
                  to={`/properties/${property.id}`}
                  className="flex min-h-touch items-center gap-1 text-[1.3125rem] font-bold text-blue-800"
                >
                  <Eye className="h-5 w-5" />
                  詳情及圖片
                </Link>
              </div>
            )}
          </div>

          <div className="mb-5 flex flex-wrap gap-2">
            {property.tags.map((tag) => (
              <span key={tag} className="bg-[#f5f3ee] px-2.5 py-1 text-[11px] font-semibold text-slate-600">
                {tag}
              </span>
            ))}
          </div>

          <div className="mb-5 mt-auto flex items-end justify-between gap-3">
            <div>
              <span className="mb-1 block text-xs text-slate-400">
                {property.listingType === 'rent' ? '每月租金' : property.listingType === 'sale_or_rent' ? '售價 / 租金' : '意向價'}
              </span>
              {property.listingType === 'sale_or_rent' ? (
                <div className="flex flex-col gap-0.5">
                  <span className="text-xl font-bold text-slate-900">售 {formatPrice(property.price, 'HKD')}</span>
                  <span className="text-xl font-bold text-slate-900">租 {formatPrice(property.rentPrice || 0, 'HKD / 月')}</span>
                </div>
              ) : (
                <span className="text-2xl font-bold text-slate-900">{displayPrice}</span>
              )}
            </div>
            {!isGrid && (
              <div className="flex items-center gap-3">
                <ShareButton
                  url={getShareUrl(property.id)}
                  title={property.title}
                  description={`${property.location} · ${property.address}`}
                />
                <button
                  type="button"
                  onClick={() => setShowMap(true)}
                  className="flex min-h-touch items-center gap-1 text-sm font-bold text-slate-600 hover:text-blue-800"
                >
                  <Map className="h-4 w-4" />
                  地圖
                </button>
                <Link
                  to={`/properties/${property.id}`}
                  className="flex min-h-touch items-center gap-1 text-sm font-bold text-blue-800"
                >
                  <Eye className="h-4 w-4" />
                  詳情及圖片
                </Link>
              </div>
            )}
          </div>

          <a
            href={whatsappUrl}
            data-wa-label={agent?.conversionLabel || ''}
            target="_blank"
            rel="noreferrer"
            onClick={() => trackEvent({ action: 'whatsapp_contact', propertyId: property.id, agentId: property.agentId })}
            className="flex min-h-[52px] w-full items-center justify-center gap-2.5 rounded-full bg-[#1f9d61] px-4 text-sm font-bold text-white transition-colors hover:bg-[#17814e]"
            aria-label={`透過 WhatsApp 聯絡 ${agent?.name || ''} 查詢 ${property.title}`}
          >
            <MessageCircle className="h-5 w-5" fill="currentColor" />
            WhatsApp 聯絡 {agent?.name || '我們'}
          </a>
          {agent && <p className="mt-2 text-center text-[11px] text-slate-400">由 {agent.title} 專責跟進</p>}
        </div>
      </motion.article>

      <MapModal
        isOpen={showMap}
        onClose={() => setShowMap(false)}
        title={property.title}
        address={property.address}
        mapLink={property.mapLink}
      />
    </>
  );
};
